import { apiClient } from '@wtf/shared/api/utils/api-client'
import {
    signIn as firebaseSignIn,
    signOut as firebaseSignOut,
    // signInWithGoogle is used by the frontend, keep export in utils/firebase
    sendPasswordReset,
    confirmPasswordResetCode,
} from '@/auth/firebase'
import { User } from 'firebase/auth'
import {
    AuthResponse,
    LoginRequest,
    RegisterUserRequest,
    RegisterStylistRequest,
    GoogleAuthCallbackRequest,
    GoogleAuthCallbackResponse,
    GoogleCompleteRegistrationRequest,
    AppleAuthCallbackRequest,
    AppleAuthCallbackResponse,
    AppleCompleteRegistrationRequest,
} from '@wtf/shared'

const BASE_ENDPOINT = 'api/auth'

/**
 * Register a new regular user (email/password)
 * Uses the backend API
 */
export async function registerUser(
    data: RegisterUserRequest
): Promise<AuthResponse> {
    return apiClient<AuthResponse>(
        `${BASE_ENDPOINT}/register/user`,
        'POST',
        data
    )
}

/**
 * Register a new stylist (email/password)
 * Uses the backend API
 */
export async function registerStylist(
    data: RegisterStylistRequest
): Promise<AuthResponse> {
    return apiClient<AuthResponse>(
        `${BASE_ENDPOINT}/register/stylist`,
        'POST',
        data
    )
}

/**
 * Login with email and password
 * Uses Firebase Authentication directly
 */
export async function login(data: LoginRequest): Promise<User> {
    return firebaseSignIn(data.email, data.password)
}

/**
 * Handle Google Sign-In callback step.
 * Calls the backend `/api/auth/google/callback` endpoint.
 * Assumes the frontend has already signed in with Firebase (using signInWithGoogle)
 * and obtained the idToken.
 */
export async function googleAuthCallback(
    data: GoogleAuthCallbackRequest
): Promise<GoogleAuthCallbackResponse> {
    // Backend verifies token, checks if user exists, returns status
    return apiClient<GoogleAuthCallbackResponse>(
        `${BASE_ENDPOINT}/google/callback`,
        'POST',
        data
    )
}

/**
 * Complete registration for a Google Sign-In user.
 * Calls the backend `/api/auth/google/complete-registration` endpoint.
 * Creates the user record after they provide role, username, etc.
 * Assumes the frontend provides the original idToken obtained during sign-in.
 */
export async function googleCompleteRegistration(
    data: GoogleCompleteRegistrationRequest
): Promise<AuthResponse> {
    // Backend creates user, returns standard AuthResponse on success
    return apiClient<AuthResponse>(
        `${BASE_ENDPOINT}/google/complete-registration`,
        'POST',
        data
    )
}

/**
 * Handle Apple Sign-In callback step.
 * Calls the backend `/api/auth/apple/callback` endpoint.
 * Assumes the frontend has already signed in with Firebase (using signInWithApple)
 * and obtained the idToken.
 */
export async function appleAuthCallback(
    data: AppleAuthCallbackRequest
): Promise<AppleAuthCallbackResponse> {
    // Backend verifies token, checks if user exists, returns status
    return apiClient<AppleAuthCallbackResponse>(
        `${BASE_ENDPOINT}/apple/callback`,
        'POST',
        data
    )
}

/**
 * Complete registration for an Apple Sign-In user.
 * Calls the backend `/api/auth/apple/complete-registration` endpoint.
 * Creates the user record after they provide role, username, etc.
 * Assumes the frontend provides the original idToken obtained during sign-in.
 */
export async function appleCompleteRegistration(
    data: AppleCompleteRegistrationRequest
): Promise<AuthResponse> {
    // Backend creates user, returns standard AuthResponse on success
    return apiClient<AuthResponse>(
        `${BASE_ENDPOINT}/apple/complete-registration`,
        'POST',
        data
    )
}

/**
 * Logout the current user
 * Uses Firebase Authentication directly
 */
export async function logout(): Promise<void> {
    return firebaseSignOut()
}

/**
 * Send a password reset email to the user's email address
 * Uses Firebase Authentication directly
 */
export async function forgotPassword(data: { email: string }): Promise<void> {
    return sendPasswordReset(data.email)
}

/**
 * Reset the user's password using the password reset code
 * Uses Firebase Authentication directly
 */
export async function resetPassword(data: {
    oobCode: string
    newPassword: string
}): Promise<void> {
    return confirmPasswordResetCode(data.oobCode, data.newPassword)
}

/**
 * Check if the current authenticated user is an admin
 * Requires authentication token
 */
export async function checkAdminStatus(): Promise<{ isAdmin: boolean }> {
    return apiClient<{ isAdmin: boolean }>(`${BASE_ENDPOINT}/check-admin`)
}

/**
 * Check if an email already exists in the system
 */
export async function checkEmailExists(email: string): Promise<boolean> {
    const response = await apiClient<{ exists: boolean }>(
        `${BASE_ENDPOINT}/check-email`,
        'POST',
        { email }
    )
    return response.exists
}

export const authApi = {
    registerUser,
    registerStylist,
    login,
    googleAuthCallback,
    googleCompleteRegistration,
    appleAuthCallback,
    appleCompleteRegistration,
    logout,
    forgotPassword,
    resetPassword,
    checkAdminStatus,
    checkEmailExists,
}
